/* Motion is optional. Native content remains readable if enhancement is unavailable. */
class ConceptAutoGallery extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.gallery = this.querySelector('concept-carousel');
    this.toggle = this.querySelector('[data-auto-toggle]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = !this.hasAttribute('data-auto-enabled') || Boolean(navigator.connection?.saveData);
    this.hovered = false;
    this.visible = false;
    this.querySelector('[data-motion-controls]').hidden = false;
    this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.sync(); }, { signal });
    this.addEventListener('mouseenter', () => { this.hovered = true; this.sync(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.sync(); }, { signal });
    this.addEventListener('focusin', (event) => { if (event.target !== this.toggle) this.pause(); }, { signal });
    this.addEventListener('pointerdown', (event) => { if (!event.target.closest('[data-auto-toggle]')) this.pause(); }, { signal });
    this.addEventListener('keydown', (event) => { if (event.target !== this.toggle) this.pause(); }, { signal });
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.motion.addEventListener('change', () => this.sync(), { signal });
    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.sync(); }, { threshold: 0.15 });
    this.observer.observe(this);
    this.sync();
  }
  pause() { this.paused = true; this.sync(); }
  sync() {
    window.clearTimeout(this.timer);
    const permitted = !this.motion.matches;
    this.toggle.hidden = !permitted;
    this.toggle.textContent = this.paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    this.toggle.setAttribute('aria-pressed', String(!this.paused));
    if (this.paused || this.hovered || !this.visible || document.hidden || !permitted) return;
    this.timer = window.setTimeout(() => {
      if (typeof this.gallery.show === 'function' && this.gallery.maxIndex > 0) {
        const next = this.gallery.index >= this.gallery.maxIndex ? 0 : this.gallery.index + 1;
        this.gallery.show(next, false);
      }
      this.sync();
    }, 4000);
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); window.clearTimeout(this.timer); }
}

class ConceptHouse extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.controls = Array.from(this.querySelectorAll('[data-room]'));
    this.copies = Array.from(this.querySelectorAll('[data-room-copy]'));
    this.querySelector('[data-house-controls]').hidden = false;
    this.controls.forEach((button) => button.addEventListener('click', () => this.show(button.dataset.room), { signal: this.abort.signal }));
    this.show('white');
  }
  show(room) {
    this.controls.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.room === room)));
    this.copies.forEach((copy) => {
      copy.hidden = false;
      copy.dataset.active = String(copy.dataset.roomCopy === room);
    });
  }
  disconnectedCallback() { this.abort?.abort(); }
}

class ConceptDayStory extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.index = 0;
    this.playing = false;
    this.frames = Array.from(this.querySelectorAll('[data-day-frame]'));
    this.choices = Array.from(this.querySelectorAll('[data-day-go]'));
    this.play = this.querySelector('[data-day-play]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.setAttribute('data-enhanced', '');
    this.querySelector('[data-day-controls]').hidden = false;
    this.choices.forEach((button) => button.addEventListener('click', () => { this.stop(); this.show(Number(button.dataset.dayGo)); }, { signal }));
    this.play.addEventListener('click', () => {
      if (this.playing) this.stop();
      else if (!this.motion.matches) { this.playing = true; this.updateLabel(); this.schedule(); }
    }, { signal });
    this.addEventListener('focusin', (event) => { if (event.target !== this.play) this.stop(); }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, { signal });
    this.motion.addEventListener('change', () => { this.stop(); this.play.hidden = this.motion.matches; }, { signal });
    this.observer = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) this.stop(); }, { threshold: 0.05 });
    this.observer.observe(this);
    this.play.hidden = this.motion.matches;
    this.show(0);
  }
  show(index) {
    this.index = Math.max(0, Math.min(this.frames.length - 1, index));
    this.dataset.day = String(this.index);
    this.frames.forEach((frame, i) => { frame.toggleAttribute('data-visible', i === this.index); frame.setAttribute('aria-hidden', String(i !== this.index)); });
    this.choices.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.index)));
  }
  schedule() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => { this.show((this.index + 1) % this.frames.length); if (this.playing) this.schedule(); }, 7000);
  }
  updateLabel() { this.play.textContent = this.playing ? this.play.dataset.labelPause : this.play.dataset.labelPlay; this.play.setAttribute('aria-pressed', String(this.playing)); }
  stop() { window.clearTimeout(this.timer); this.playing = false; this.updateLabel(); }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); window.clearTimeout(this.timer); }
}

class ConceptVideoPlayer extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains('lamp-film__player--inverted')) return;
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.video = this.querySelector('video');
    this.playButton = this.querySelector('[data-video-play]');
    this.muteButton = this.querySelector('[data-video-mute]');
    this.progress = this.querySelector('[data-video-progress]');
    this.controls = this.querySelector('[data-video-controls]');
    if (!this.video || !this.playButton || !this.muteButton || !this.progress || !this.controls) return;

    this.video.controls = false;
    this.controls.hidden = false;
    this.playButton.addEventListener('click', () => this.togglePlayback(), { signal });
    this.muteButton.addEventListener('click', () => {
      this.video.muted = !this.video.muted;
      this.sync();
    }, { signal });
    this.progress.addEventListener('input', () => {
      if (!Number.isFinite(this.video.duration) || this.video.duration <= 0) return;
      this.video.currentTime = (Number(this.progress.value) / 100) * this.video.duration;
    }, { signal });
    for (const eventName of ['loadedmetadata', 'timeupdate', 'play', 'pause', 'ended', 'volumechange']) {
      this.video.addEventListener(eventName, () => this.sync(), { signal });
    }
    this.sync();
  }

  async togglePlayback() {
    if (!this.video) return;
    if (!this.video.paused) {
      this.video.pause();
      return;
    }
    try {
      await this.video.play();
    } catch (error) {
      if (error.name !== 'AbortError') this.video.controls = true;
    }
  }

  sync() {
    const playing = !this.video.paused && !this.video.ended;
    this.playButton.textContent = playing ? this.dataset.labelPause : this.dataset.labelPlay;
    this.muteButton.textContent = this.video.muted ? this.dataset.labelUnmute : this.dataset.labelMute;
    this.muteButton.setAttribute('aria-pressed', String(this.video.muted));
    const duration = Number.isFinite(this.video.duration) ? this.video.duration : 0;
    const current = Math.round(this.video.currentTime);
    const percent = duration > 0 ? (this.video.currentTime / duration) * 100 : 0;
    this.progress.value = String(percent);
    this.progress.disabled = duration <= 0;
    this.progress.setAttribute('aria-valuetext', this.dataset.labelTime.replace('__CURRENT__', String(current)).replace('__DURATION__', String(Math.round(duration))));
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.video?.pause();
  }
}

if (!customElements.get('concept-auto-gallery')) customElements.define('concept-auto-gallery', ConceptAutoGallery);
if (!customElements.get('concept-house')) customElements.define('concept-house', ConceptHouse);
if (!customElements.get('concept-day-story')) customElements.define('concept-day-story', ConceptDayStory);
if (!customElements.get('concept-video-player')) customElements.define('concept-video-player', ConceptVideoPlayer);
